import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@/stores/authStore'
import { useTenantStore } from '@/stores/tenantStore'
import { toast } from '@/hooks/use-toast'

const endpoint =
  import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:4000/graphql'

// Create HTTP link
const httpLink = createHttpLink({
  uri: endpoint,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Create auth link to attach token to requests
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(ACCESS_TOKEN)
  const refreshToken = localStorage.getItem(REFRESH_TOKEN)
  const tenantId = useTenantStore.getState().selectedTenantId
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'x-session-token': refreshToken || '',
      'Content-Type': 'application/json',
      ...(tenantId && { 'x-tenant-id': tenantId }),
    },
  }
})

// Create error link to handle GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Allow operations to suppress global GraphQL error handling
  const { suppressGraphQLErrors } = operation.getContext?.() ?? {}
  const opName = operation.operationName
  const shouldSuppress =
    suppressGraphQLErrors || opName === 'OrganizationServiceGetByDomain'

  if (graphQLErrors && !shouldSuppress) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
      )
      if (import.meta.env.DEV) {
        toast({
          title: 'GraphQL error',
          description: message || 'Something went wrong',
          variant: 'destructive',
        })
      }
    })
  }

  if (networkError && !shouldSuppress) {
    // eslint-disable-next-line no-console
    console.error(`[Network error]: ${networkError}`)
    if (networkError.message.includes('401')) {
      window.location.reload()
    }

    toast({
      title: 'Network error',
      description: (networkError as Error).message || 'Something went wrong',
      variant: 'destructive',
    })
    // TODO: Handle authentication errors
  }
})

// Function to update headers (e.g., for authentication)
export const updateGraphQLClientHeaders = (headers: Record<string, string>) => {
  Object.assign(apolloClient.defaultContext.headers, headers)
}

// Function to set authorization token
export const setApolloAuthToken = (token: string) => {
  updateGraphQLClientHeaders({
    Authorization: `Bearer ${token}`,
  })
}

// Function to remove authorization token
export const removeApolloAuthToken = () => {
  delete apolloClient.defaultContext.headers?.Authorization
}

// Create Apollo Client instance
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})
